<template>
  <main class="batch-page">
    <header class="batch-page-heading">
      <div>
        <span class="batch-kicker">MON ORGANISATION EN CUISINE</span>
        <h1>Préparer plusieurs repas à l’avance</h1>
        <p>Choisissez des repas de votre semaine. MMM organise les préparations et les cuissons sans modifier vos menus.</p>
      </div>
      <router-link to="/mes-menus">← Retour à mes menus</router-link>
    </header>

    <div class="batch-week-switch">
      <button type="button" :disabled="busy || loading" aria-label="Semaine précédente" @click="changeWeek(-1)">←</button>
      <strong>Semaine {{ week }}</strong>
      <button type="button" :disabled="busy || loading" aria-label="Semaine suivante" @click="changeWeek(1)">→</button>
    </div>

    <div class="batch-count-control">
      <label for="batch-meal-count">Combien de repas souhaitez-vous préparer à l’avance ?</label>
      <select id="batch-meal-count" :value="selectionLimit" :disabled="busy || loading || countSaving" @change="updateSelectionLimit">
        <option :value="2">2 repas</option><option :value="3">3 repas</option><option :value="4">4 repas</option><option :value="5">5 repas</option>
      </select>
      <p>Vous pourrez ajuster votre sélection juste en dessous. Vos menus restent inchangés.</p>
      <p v-if="countFeedback" class="batch-count-feedback" :class="{ error: countSaveError }" role="status">{{ countFeedback }}</p>
    </div>
    <div v-if="!loading && menus.length" class="batch-plan-quick-action">
      <button type="button" class="button" :disabled="busy || countSaving || selected.length !== selectionLimit" @click="plan ? scrollToPlan() : generatePlan()">
        {{ busy ? 'Préparation du plan…' : 'Voir mon plan de préparation' }}
      </button>
      <span v-if="selected.length < selectionLimit">Sélectionnez {{ selectionLimit - selected.length }} repas pour afficher le plan.</span>
      <span v-else-if="selected.length > selectionLimit">Désélectionnez {{ selected.length - selectionLimit }} repas pour afficher le plan.</span>
    </div>
    <p v-if="feedback" class="batch-feedback" role="status">{{ feedback }}</p>
    <p v-if="loading" role="status">Chargement des repas…</p>
    <p v-if="error" class="batch-error" role="alert">{{ error }}</p>

    <template v-if="!loading && menus.length">
      <div class="batch-table-scroll">
        <table class="batch-selection-table">
          <caption>Quels repas souhaitez-vous préparer à l’avance ?</caption>
          <thead><tr><th scope="col">Jour</th><th scope="col">Midi</th><th scope="col">Soir</th></tr></thead>
          <tbody>
            <tr v-for="menu in menus" :key="menu.date">
              <th scope="row">{{ dayLabel(menu.date) }}</th>
              <td v-for="meal in ['lunch', 'dinner']" :key="meal">
                <label v-if="cell(menu, meal).item && !cell(menu, meal).disabled"
                  class="batch-meal-check" :class="{ selected: selected.includes(selectionKey(menu.date, meal)) }">
                  <input v-model="selected" type="checkbox" :value="selectionKey(menu.date, meal)" :disabled="busy || (!selected.includes(selectionKey(menu.date, meal)) && selected.length >= selectionLimit)" @change="saveSelection" />
                  <span>
                    <button type="button" class="batch-recipe-link" :aria-label="'Ouvrir la recette complète : ' + cell(menu, meal).item.dish.name" @click.stop="openRecipe(cell(menu, meal).item.dish)">{{ cell(menu, meal).item.dish.name }}</button>
                    <small>{{ meal === 'lunch' ? 'Déjeuner' : 'Dîner' }} · repas de la semaine</small>
                  </span>
                </label>
                <span v-else class="batch-out-of-scope">
                  {{ cell(menu, meal).reason || 'Pas de plat prévu' }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="batch-build-bar">
        <div>
          <strong>{{ selected.length }} / {{ selectionLimit }} repas sélectionnés</strong>
          <p v-if="selected.length > selectionLimit" class="batch-count-feedback error">Désélectionnez {{ selected.length - selectionLimit }} repas pour respecter ce nombre.</p>
          <p>Les repas à base de restes et ceux pris à l’extérieur ne sont pas sélectionnables. Les menus restent inchangés.</p>
        </div>
      </div>

      <section v-if="plan" ref="preparation" class="batch-preparation-area" tabindex="-1">
        <div class="batch-kicker">VOTRE SÉANCE</div>
        <h2>Un ordre de préparation pour gagner du temps</h2>
        <p class="batch-plan-intro">Plan établi à partir des recettes de votre semaine. Les étapes restent propres à chaque recette ; seuls les ingrédients qui s’y prêtent sont regroupés.</p>
        <p v-if="plan.guide.chronology_warning" class="batch-warning">{{ plan.guide.chronology_warning }}</p>

        <section class="batch-guide-section">
          <h3>Liste des ingrédients à préparer</h3>
          <p class="batch-section-help">Les quantités totales sont visibles tout de suite. Cliquez sur un ingrédient pour voir les recettes concernées et les détails de préparation.</p>
          <details v-for="ingredient in allCoreIngredients" :key="ingredient.key + ':' + ingredient.prep_group" :data-ingredient-key="ingredient.key + ':' + ingredient.prep_group" class="batch-ingredient batch-ingredient-details">
            <summary>
              <span class="batch-ingredient-name"><strong>{{ ingredient.name }}</strong><small>{{ ingredient.prep_group }}</small></span>
              <strong class="batch-ingredient-total">{{ quantity(ingredient) }}</strong>
            </summary>
            <div class="batch-ingredient-detail">
              <p><strong>{{ ingredient.action }}</strong> · {{ ingredient.instruction }}</p>
              <ul v-if="ingredient.uses?.length" class="batch-ingredient-uses">
                <li v-for="use in ingredient.uses" :key="use.key">
                  <button type="button" class="batch-recipe-link" :aria-label="'Ouvrir la recette complète : ' + use.dish_name" @click.stop="openRecipeByName(use.dish_name)">{{ use.dish_name }}</button><strong>{{ quantity(use) }}</strong>
                </li>
              </ul>
            </div>
          </details>
          <p v-if="!allCoreIngredients.length" class="batch-empty">Aucun ingrédient principal à afficher.</p>
        </section>

        <section class="batch-guide-section">
          <h3>Étapes de préparation et de cuisson</h3>
          <p v-if="workflowTotalSteps" class="batch-progress">{{ workflowRemainingSteps }} étape{{ workflowRemainingSteps > 1 ? 's' : '' }} restante{{ workflowRemainingSteps > 1 ? 's' : '' }} sur {{ workflowTotalSteps }}.</p>
          <p v-if="workflowTotalSteps && workflowRemainingSteps === 0" class="batch-complete">Toutes les étapes sont terminées.</p>
          <article v-for="phase in remainingWorkflowPhases" :key="phase.key" class="batch-phase">
            <h4><span>{{ phase.phaseIndex + 1 }}</span>{{ phase.title }}</h4>
            <p>{{ phase.note }}</p>
            <div v-for="task in phase.tasks" :key="task.key" class="batch-task">
              <button type="button" class="batch-recipe-link batch-task-recipe-link" :aria-label="'Ouvrir la recette complète : ' + task.dish_name" @click="openRecipeByName(task.dish_name)">{{ task.dish_name }}</button>
              <ol v-if="task.instructions.length">
                <li v-for="instruction in task.instructions" :key="instruction.index">
                  <label class="batch-step-check" :class="{ 'is-complete': isStepCompleted(phase.key, task.key, instruction.index) }">
                    <input type="checkbox" :checked="isStepCompleted(phase.key, task.key, instruction.index)" @change="toggleStep(phase.key, task.key, instruction.index)" />
                    <span>{{ instruction.text }}</span>
                  </label>
                </li>
              </ol>
            </div>
          </article>
          <p v-for="missing in plan.guide.cooking_workflow?.missing_recipes || []" :key="missing" class="batch-warning">
            Les étapes de « <button type="button" class="batch-recipe-link" :aria-label="'Ouvrir la recette complète : ' + missing" @click="openRecipeByName(missing)">{{ missing }}</button> » ne sont pas disponibles. Retrouvez-les dans la fiche recette.
          </p>
        </section>

        <section class="batch-guide-section">
          <h3>À ranger et à terminer au moment du repas</h3>
          <article v-for="finish in plan.guide.meal_finishes" :key="finish.key" class="batch-finish">
            <h4>{{ dayLabel(finish.date) }} · {{ finish.meal === 'lunch' ? 'midi' : 'soir' }} —
              <button type="button" class="batch-recipe-link" :aria-label="'Ouvrir la recette complète : ' + finish.dish_name" @click="openRecipe(finish.dish_id)">{{ finish.dish_name }}</button>
            </h4>
            <p><strong>{{ finish.storage_label }}.</strong> {{ finish.storage_detail }}</p>
            <p>{{ finish.finish }}</p>
          </article>
        </section>

        <section v-if="plan.guide.roadmap?.length" class="batch-guide-section batch-roadmap">
          <h3>Les bons repères</h3>
          <article v-for="(item,index) in plan.guide.roadmap" :key="item.key">
            <h4>{{ index + 1 }}. {{ item.title }}</h4><p>{{ item.text }}</p>
          </article>
        </section>
        <ul v-if="plan.guide.tips?.length" class="batch-tips"><li v-for="tip in plan.guide.tips" :key="tip">{{ tip }}</li></ul>
      </section>
    </template>

    <div v-else-if="!loading && !error" class="batch-empty-week">
      <p>Vos menus ne sont pas disponibles pour cette semaine.</p>
      <router-link class="button" to="/mes-menus">Voir mes menus</router-link>
    </div>
  </main>
</template>

<script>
import { api } from '@/api';
import { userState } from '@/utils/store';

const WEEKDAYS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];

function currentWeek() {
  const day = new Date();
  day.setHours(12, 0, 0, 0);
  day.setDate(day.getDate() + 3 - (day.getDay() + 6) % 7);
  const first = new Date(day.getFullYear(), 0, 4);
  return 1 + Math.round(((day - first) / 86400000 - 3 + (first.getDay() + 6) % 7) / 7);
}

export default {
  name: 'MyBatchCooking',
  data() {
    return {
      week: Number(this.$route.query.week) || currentWeek(),
      selectionLimit: (() => {
        const fromRoute = Number(this.$route.query.count);
        const saved = Number(userState.me?.consumer_account?.meal_prep_group_size);
        return [2, 3, 4, 5].includes(fromRoute) ? fromRoute : ([2, 3, 4, 5].includes(saved) ? saved : 3);
      })(),
      countSaving: false,
      countFeedback: '',
      countSaveError: false,
      completedSteps: [],
      menus: [],
      loading: true,
      busy: false,
      error: '',
      feedback: '',
      selected: [],
      plan: null,
    };
  },
  computed: {
    householdKey() { return String(userState.me?.consumer_account?.id || userState.me?.id || 'anonymous'); },
    draftKey() { return 'mmm-meal-prep-v3:' + this.householdKey + ':' + this.week; },
    stepProgressStorageKey() { return 'mmm-meal-prep-progress-v1:' + this.householdKey + ':' + this.week + ':' + this.selected.slice().sort().join(','); },
    returnStateStorageKey() { return 'mmm-meal-prep-return-v1:' + this.householdKey + ':' + this.week; },
    allCoreIngredients() {
      const shared = this.visibleCoreIngredients(this.plan?.guide?.shared_ingredients)
        .map(row => ({ ...row, prep_group: 'À préparer ensemble' }));
      const separate = this.visibleCoreIngredients(this.plan?.guide?.single_ingredients)
        .map(row => ({ ...row, prep_group: 'Pour une seule recette' }));
      return [...shared, ...separate].sort((a, b) =>
        (a.prep_group === b.prep_group ? 0 : a.prep_group === 'À préparer ensemble' ? -1 : 1)
        || a.name.localeCompare(b.name, 'fr'));
    },
    remainingWorkflowPhases() {
      const phases = this.plan?.guide?.cooking_workflow?.phases || [];
      return phases.map((phase, phaseIndex) => ({
        ...phase,
        phaseIndex,
        tasks: (phase.tasks || []).map(task => ({
          ...task,
          instructions: (task.instructions || []).map((text, index) => ({ text, index })),
        })),
      }));
    },
    completedWorkflowSteps() {
      const out = [];
      const phases = this.plan?.guide?.cooking_workflow?.phases || [];
      phases.forEach((phase, phaseIndex) => (phase.tasks || []).forEach(task => (task.instructions || []).forEach((text, index) => {
        if (this.isStepCompleted(phase.key, task.key, index)) out.push({
          key: this.stepKey(phase.key, task.key, index), phaseKey: phase.key, phaseIndex,
          phaseTitle: phase.title, taskKey: task.key, dishName: task.dish_name, index, text,
        });
      })));
      return out;
    },
    workflowTotalSteps() {
      return (this.plan?.guide?.cooking_workflow?.phases || [])
        .reduce((total, phase) => total + (phase.tasks || []).reduce((sum, task) => sum + (task.instructions || []).length, 0), 0);
    },
    workflowRemainingSteps() {
      return Math.max(0, this.workflowTotalSteps - this.completedSteps.length);
    },
  },
  mounted() {
    this.load().then(async () => {
      if (await this.restoreReturnState()) return;
      if (String(this.$route.query.viewPlan) !== '1') return;
      const query = { ...this.$route.query };
      delete query.viewPlan;
      this.$router.replace({ query }).catch(() => {});
      if (this.selected.length === this.selectionLimit) this.generatePlan();
    });
  },
  methods: {
    saveReturnState() {
      const table = this.$el.querySelector('.batch-table-scroll');
      const openIngredients = Array.from(this.$el.querySelectorAll('details.batch-ingredient-details[open]'))
        .map(details => details.dataset.ingredientKey).filter(Boolean);
      const state = {
        selected: this.selected,
        plan: this.plan,
        completedSteps: this.completedSteps,
        scrollY: window.scrollY || 0,
        tableScrollLeft: table ? table.scrollLeft : 0,
        openIngredients,
      };
      try { sessionStorage.setItem(this.returnStateStorageKey, JSON.stringify(state)); } catch (_) { /* La navigation reste possible sans restauration de position. */ }
    },
    async restoreReturnState() {
      let raw = null;
      try {
        raw = sessionStorage.getItem(this.returnStateStorageKey);
        if (!raw) return false;
        sessionStorage.removeItem(this.returnStateStorageKey);
      } catch (_) { return false; }
      try {
        const state = JSON.parse(raw);
        if (Array.isArray(state.selected)) this.selected = state.selected;
        if (Object.prototype.hasOwnProperty.call(state, 'plan')) this.plan = state.plan;
        if (Array.isArray(state.completedSteps)) this.completedSteps = state.completedSteps;
        await this.$nextTick();
        const open = new Set(state.openIngredients || []);
        this.$el.querySelectorAll('details.batch-ingredient-details').forEach(details => {
          details.open = open.has(details.dataset.ingredientKey);
        });
        const table = this.$el.querySelector('.batch-table-scroll');
        if (table) table.scrollLeft = Number(state.tableScrollLeft) || 0;
        window.scrollTo(0, Number(state.scrollY) || 0);
        return true;
      } catch (_) {
        return false;
      }
    },
    openRecipeByName(name) {
      for (const key of this.selected) {
        const split = key.lastIndexOf('_');
        const date = key.slice(0, split);
        const meal = key.slice(split + 1);
        const menu = this.menus.find(item => item.date === date);
        const dish = menu && this.cell(menu, meal).item?.dish;
        if (dish && dish.name === name) return this.openRecipe(dish);
      }
    },
    openRecipe(dish) {
      const id = typeof dish === 'object' ? dish?.id : dish;
      if (!id) return;
      this.saveReturnState();
      this.$router.push({ name: 'dish', params: { id: String(id) } }).catch(() => {});
    },
    visibleCoreIngredients(rows) {
      return (rows || []).filter(row => !/\beau\s+du\s+robinet\b/i.test(String(row.name || '')));
    },
    stepKey(phaseKey, taskKey, index) { return [phaseKey, taskKey, index].join('::'); },
    isStepCompleted(phaseKey, taskKey, index) { return this.completedSteps.includes(this.stepKey(phaseKey, taskKey, index)); },
    persistStepProgress() {
      try { localStorage.setItem(this.stepProgressStorageKey, JSON.stringify(this.completedSteps)); } catch (_) { /* La progression reste visible pendant la séance. */ }
    },
    toggleStep(phaseKey, taskKey, index) {
      const key = this.stepKey(phaseKey, taskKey, index);
      this.completedSteps = this.completedSteps.includes(key)
        ? this.completedSteps.filter(item => item !== key)
        : [...this.completedSteps, key];
      this.persistStepProgress();
    },
    restoreStep(phaseKey, taskKey, index) {
      const key = this.stepKey(phaseKey, taskKey, index);
      this.completedSteps = this.completedSteps.filter(item => item !== key);
      this.persistStepProgress();
    },
    loadStepProgress() {
      try {
        const saved = JSON.parse(localStorage.getItem(this.stepProgressStorageKey) || '[]');
        this.completedSteps = Array.isArray(saved) ? saved : [];
      } catch (_) { this.completedSteps = []; }
    },
    selectionKey(date, meal) { return date + '_' + meal; },
    dayLabel(value) {
      const date = new Date(String(value) + 'T12:00:00');
      const weekday = WEEKDAYS[(date.getDay() + 6) % 7];
      return weekday.charAt(0).toUpperCase() + weekday.slice(1) + ' ' + date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    },
    cell(menu, meal) {
      const origin = meal === 'lunch' ? menu.lunch_origin_type : menu.dinner_origin_type;
      if (origin === 'outside') return { disabled: true, reason: 'Repas pris à l’extérieur' };
      if (origin === 'leftovers') return { disabled: true, reason: 'Repas à base de restes' };
      const dishType = meal === 'lunch' ? 'lunch_main' : 'diner_main';
      const item = (menu.items || []).find(value => value.dish_type === dishType);
      if (!item) return { disabled: true, reason: 'Pas de plat principal prévu' };
      if (item.meal_origin_type === 'leftovers') return { disabled: true, reason: 'Repas à base de restes' };
      return { item, disabled: false };
    },
    async updateSelectionLimit(event) {
      const next = Number(event.target.value);
      if (![2, 3, 4, 5].includes(next) || next === this.selectionLimit) return;
      const previous = this.selectionLimit;
      this.countSaving = true;
      this.countFeedback = '';
      this.countSaveError = false;
      try {
        await userState.savePreferences({ meal_prep_group_size: next }, { regenerate: false, timeout: 15000 });
        this.selectionLimit = next;
        await this.$router.replace({ query: { ...this.$route.query, count: String(next) } }).catch(() => {});
        this.countFeedback = 'Nombre de repas enregistré dans vos préférences.';
      } catch (error) {
        this.selectionLimit = previous;
        event.target.value = String(previous);
        this.countSaveError = true;
        this.countFeedback = 'Impossible d’enregistrer ce nombre. Réessayez ; vos préférences n’ont pas été modifiées.';
      } finally {
        this.countSaving = false;
      }
    },
    saveSelection() {
      try { localStorage.setItem(this.draftKey, JSON.stringify(this.selected)); } catch (error) { /* La sélection reste utilisable sans stockage local. */ }
      this.loadStepProgress();
      this.plan = null;
      this.feedback = '';
    },
    async load() {
      this.loading = true;
      this.error = '';
      this.plan = null;
      try {
        const response = await api.get('/my-menus/' + this.week + '/', { timeout: 15000 });
        this.menus = Array.isArray(response.data) ? response.data : [];
        const raw = JSON.parse(localStorage.getItem(this.draftKey) || '[]');
        const available = new Set();
        this.menus.forEach(menu => ['lunch', 'dinner'].forEach(meal => {
          if (this.cell(menu, meal).item) available.add(this.selectionKey(menu.date, meal));
        }));
        this.selected = Array.isArray(raw) ? raw.filter(key => available.has(key)).slice(0, this.selectionLimit) : [];
        this.loadStepProgress();
      } catch (error) {
        this.error = error.response?.data?.detail || 'Impossible de charger les repas. Réessayez dans un instant.';
      } finally {
        this.loading = false;
      }
    },
    async changeWeek(delta) {
      if (this.busy || this.loading) return;
      this.week += delta;
      if (this.week < 1) this.week = 53;
      if (this.week > 53) this.week = 1;
      await this.$router.replace({ query: { ...this.$route.query, week: String(this.week) } }).catch(() => {});
      await this.load();
    },
    quantity(row) {
      const number = String(Math.round(Number(row.quantity || 0)));
      const unit = row.unit === 'piece' ? 'pièce(s)' : row.unit === 'pincee' ? 'pincée(s)' : row.unit;
      return number + ' ' + unit;
    },
    scrollToPlan() {
      this.$refs.preparation?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.$refs.preparation?.focus({ preventScroll: true });
    },
    async generatePlan() {
      if (this.busy || this.selected.length !== this.selectionLimit) return;
      this.busy = true;
      this.error = '';
      this.feedback = '';
      this.plan = null;
      try {
        const meals = this.selected.map(key => {
          const split = key.lastIndexOf('_');
          return { date: key.slice(0, split), meal: key.slice(split + 1) };
        });
        const response = await api.post('/meal-prep-plan/' + this.week + '/', { meals }, { timeout: 60000 });
        this.plan = response.data;
        this.feedback = 'Votre plan est prêt. Vos menus n’ont pas été modifiés.';
        await this.$nextTick();
        this.scrollToPlan();
      } catch (error) {
        this.error = error.response?.data?.detail || error.response?.data?.message || 'Le plan de préparation n’a pas pu être créé. Vos menus restent inchangés.';
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.batch-plan-quick-action{display:flex;align-items:center;gap:12px;flex-wrap:wrap;margin:14px 0 18px}
.batch-plan-quick-action .button{font-weight:700;padding:11px 18px}
.batch-plan-quick-action span{color:#65705f;font-size:13px}
.batch-count-control{display:flex;align-items:center;flex-wrap:wrap;gap:10px 14px;margin:16px 0;padding:14px 16px;border:1px solid #e1e8d9;border-radius:14px;background:#f8faf5}
.batch-count-control label{font-weight:700;color:#34452b}
.batch-count-control select{min-width:130px;padding:9px 12px;border:1px solid #cfd9c5;border-radius:9px;background:#fff;color:#34452b}
.batch-count-control p{flex-basis:100%;margin:0;color:#687360;font-size:13px}
.batch-count-control .batch-count-feedback{color:#527532}
.batch-count-control .batch-count-feedback.error,.batch-count-feedback.error{color:#a33128}
.batch-page{box-sizing:border-box;width:100%;max-width:1180px;min-width:0;margin:0 auto;padding:30px 24px;color:#283725}
.batch-page-heading{display:flex;justify-content:space-between;align-items:flex-start;gap:20px;flex-wrap:wrap}
.batch-page-heading h1{font-size:1.8rem;line-height:1.2;margin:8px 0 10px}
.batch-page-heading p{max-width:720px;margin:0;color:#566351;line-height:1.55}
.batch-page-heading a{color:#436f0e;font-weight:600}
.batch-kicker{font-size:.78rem;letter-spacing:.12em;font-weight:700;color:#436f0e}
.batch-week-switch{display:flex;align-items:center;justify-content:center;gap:20px;margin:24px 0}
.batch-week-switch button{border:1px solid #ccd8c1;border-radius:8px;background:white;padding:8px 16px}
.batch-table-scroll{overflow-x:auto}
.batch-selection-table{width:100%;border-collapse:collapse;table-layout:fixed}
.batch-selection-table caption{text-align:left;font-weight:700;font-size:1.1rem;caption-side:top;margin-bottom:12px}
.batch-selection-table th,.batch-selection-table td{border:1px solid #dce5d3;padding:12px;vertical-align:top}
.batch-selection-table thead th{background:#edf3e6}
.batch-selection-table th:first-child{width:118px}
.batch-meal-check{display:flex;align-items:flex-start;gap:12px;padding:12px;border:1px solid transparent;border-radius:10px;cursor:pointer;min-height:78px}
.batch-meal-check.selected{background:#eff6e6;border-color:#82a85c}
.batch-meal-check small{display:block;margin-top:8px;color:#63715b}
.batch-page input{width:19px;height:19px;accent-color:#436f0e;flex-shrink:0}
.batch-out-of-scope{display:block;color:#718069;font-size:.88rem;padding:12px}
.batch-build-bar{display:flex;justify-content:space-between;align-items:center;gap:20px;padding:18px;margin-top:18px;background:#f4f7ef;border-radius:12px}
.batch-build-bar p{font-size:.88rem;margin:7px 0 0;max-width:680px;color:#566351}
.batch-build-bar button{white-space:nowrap}
.batch-page button:disabled{opacity:.55;cursor:wait}
.batch-feedback{color:#315b19;font-weight:600}
.batch-error,.batch-warning{background:#fff0e8;color:#8d3818;padding:14px;border-radius:8px}
.batch-empty-week{padding:24px;text-align:center}
.batch-preparation-area{margin-top:38px;scroll-margin-top:24px;outline:none}
.batch-preparation-area h2{font-size:1.55rem;margin:8px 0}
.batch-plan-intro,.batch-section-help{color:#566351;line-height:1.5}
.batch-selected-meals,.batch-ingredient,.batch-phase,.batch-finish,.batch-roadmap article{margin:12px 0;padding:15px 17px;border:1px solid #dce5d3;border-radius:10px;background:#fff}
.batch-guide-section{margin-top:28px}
.batch-guide-section h3{margin:0 0 8px;color:#315b19}
.batch-ingredient-title{display:flex;justify-content:space-between;gap:16px}
.batch-recipe-link{border:0;padding:0;background:transparent;color:#436f0e;font:inherit;font-weight:700;text-align:left;text-decoration:underline;text-underline-offset:2px;cursor:pointer}
.batch-recipe-link:hover{color:#315b19}
.batch-task-recipe-link{display:block;margin-bottom:4px}
.batch-ingredient-title span{font-weight:700;white-space:nowrap}
.batch-ingredient p,.batch-phase p,.batch-finish p,.batch-roadmap p{margin:8px 0 0;line-height:1.5;color:#4f5953}
.batch-ingredient small{display:block;margin-top:8px;color:#63715b}
.batch-empty{padding:12px;background:#f4f7ef;border-radius:8px;color:#566351}
.batch-phase h4,.batch-finish h4,.batch-roadmap h4{margin:0;color:#34463a}
.batch-phase h4 span{display:inline-grid;place-items:center;width:25px;height:25px;margin-right:9px;border-radius:50%;background:#dcebcf;color:#315b19}
.batch-task{margin-top:13px;padding-top:12px;border-top:1px solid #e5ebdf}
.batch-task ol{padding-left:22px;line-height:1.55}
.batch-step-check{display:flex;align-items:flex-start;gap:10px;cursor:pointer}
.batch-step-check input{flex:0 0 auto;margin:2px 0 0}
.batch-step-check span{display:block}.batch-step-check.is-complete span{text-decoration:line-through;opacity:.62}
.batch-progress,.batch-complete{margin:8px 0;color:#566351;font-size:.9rem}
.batch-ingredient-details{padding:0}
.batch-ingredient-details summary{display:flex;align-items:center;justify-content:space-between;gap:14px;padding:14px 16px;cursor:pointer;list-style:none}
.batch-ingredient-details summary::-webkit-details-marker{display:none}
.batch-ingredient-name{display:grid;gap:4px}
.batch-ingredient-name small{font-size:12px;font-weight:600;color:#687360}
.batch-ingredient-total{white-space:nowrap}
.batch-ingredient-detail{padding:0 16px 14px;border-top:1px solid #e5ebdf}
.batch-ingredient-detail p{margin:10px 0;color:#4f5953;line-height:1.5}
.batch-ingredient-uses{margin:8px 0 0;padding:0;list-style:none}
.batch-ingredient-uses li{display:flex;justify-content:space-between;gap:12px;padding:7px 0;border-top:1px solid #edf0e8}
.batch-ingredient-uses li strong{white-space:nowrap}
.batch-completed-steps{margin:16px 0;padding:12px 14px;border:1px solid #dce5d3;border-radius:10px;background:#f8faf5}
.batch-completed-steps>summary{cursor:pointer;font-weight:700;color:#426128}
.batch-completed-step{display:flex;align-items:center;justify-content:space-between;gap:14px;padding:10px 0;border-top:1px solid #e5ebdf}
.batch-completed-step>div{display:grid;gap:4px}
.batch-completed-step small{color:#687360}
.batch-completed-step p{margin:0!important}
.batch-restore-step{flex:0 0 auto;border:1px solid #cfd9c5;border-radius:8px;padding:7px 10px;background:#fff;color:#426128;font-weight:700;cursor:pointer}
.batch-complete{padding:12px 14px;border-radius:9px;background:#eff6e6;color:#315b19;font-weight:700}
.batch-roadmap article{margin:10px 0}
.batch-tips{padding:15px 18px 15px 38px;background:#f4f7ef;border-radius:10px;line-height:1.6;color:#4f5953}
@media(max-width:720px){.batch-page{padding:20px 12px}.batch-selection-table{min-width:720px}.batch-build-bar{flex-direction:column;align-items:stretch}.batch-build-bar button{white-space:normal}.batch-page-heading h1{font-size:1.45rem}.batch-ingredient-title{align-items:flex-start}}
</style>
